type InstallPrompt = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};
type InstallState = { installed: boolean; canPrompt: boolean };
const initial: InstallState = { installed: false, canPrompt: false };
let state = initial, prompt: InstallPrompt | null = null, started = false;
const listeners = new Set<() => void>();
const publish = (next: InstallState) => { state = next; for (const listener of listeners) listener(); };
export const getInstallState = () => state;
export const getServerInstallState = () => initial;
export const subscribeInstall = (listener: () => void) => { listeners.add(listener); return () => { listeners.delete(listener); }; };

export function startPwa(registerWorker: boolean) {
  if (started) return;
  started = true;
  const display = window.matchMedia("(display-mode: standalone)");
  const installed = () => display.matches || !!(navigator as Navigator & { standalone?: boolean }).standalone;
  publish({ installed: installed(), canPrompt: false });
  display.addEventListener("change", () => publish({ ...state, installed: installed() }));
  window.addEventListener("beforeinstallprompt", event => {
    event.preventDefault();
    prompt = event as InstallPrompt;
    publish({ installed: installed(), canPrompt: true });
  });
  window.addEventListener("appinstalled", () => {
    prompt = null;
    publish({ installed: true, canPrompt: false });
  });
  if (registerWorker && window.isSecureContext && "serviceWorker" in navigator) {
    void navigator.serviceWorker.register("/sw.js", { scope: "/", updateViaCache: "none" }).catch(() => {
      console.warn("PWA offline support could not be registered.");
    });
  }
}

/** Must be called directly from a user click, before awaiting unrelated work. */
export async function requestAppInstall(): Promise<"accepted" | "dismissed" | "unavailable"> {
  const pending = prompt;
  if (!pending) return "unavailable";
  prompt = null;
  publish({ ...state, canPrompt: false });
  try {
    await pending.prompt();
    return (await pending.userChoice).outcome;
  } catch { return "unavailable"; }
}
